import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { GraduationCap, ChevronDown, Check, BookOpen, Users, Award, Smartphone, Lock, Zap, RefreshCw } from 'lucide-react';
import axios from 'axios';

const InstitutionSelection = () => {
  const navigate = useNavigate();
  const [institutions, setInstitutions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedInstitution, setSelectedInstitution] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  useEffect(() => {
    fetchInstitutions();
  }, []);

  const fetchInstitutions = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/institutions/public/all');
      let data = response.data;

      // Sort "Intuit Online Academy" to the top
      data.sort((a, b) => {
        if (a.name === 'Intuit Online Academy') return -1;
        if (b.name === 'Intuit Online Academy') return 1;
        return a.name.localeCompare(b.name);
      });

      setInstitutions(data);
    } catch (error) {
      console.error('Error fetching institutions:', error);
      setError('Failed to load institutions. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleInstitutionSelect = (institution) => {
    setSelectedInstitution(institution);
    setIsModalOpen(false);
  };

  const handleContinue = () => {
    if (!selectedInstitution) {
      alert('Please select your institution to continue.');
      return;
    }

    // Store selected institution in localStorage
    localStorage.setItem('selectedInstitution', JSON.stringify(selectedInstitution));

    // Navigate to landing page
    navigate('/landing');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-slate-600 font-medium">Connecting to Intuit Cloud...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6">
        <div className="max-w-md w-full bg-white rounded-xl shadow-lg p-8 text-center border border-red-100">
          <div className="w-16 h-16 bg-red-50 text-red-600 rounded-full flex items-center justify-center mx-auto mb-6">
            <Smartphone className="w-8 h-8" />
          </div>
          <h2 className="text-2xl font-bold text-slate-900 mb-2">Connectivity Issue</h2>
          <p className="text-slate-600 mb-8">{error}</p>
          <button
            onClick={fetchInstitutions}
            className="w-full bg-blue-600 text-white px-6 py-3 rounded-lg font-bold hover:bg-blue-700 transition-all"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col">
      {/* Compact Header */}
      <div className="bg-gradient-to-br from-blue-700 via-blue-800 to-indigo-900 text-white shrink-0">
        <div className="max-w-6xl mx-auto px-6 py-16 md:py-20">
          <div className="flex flex-col items-center text-center">
            <div className="mb-6">
              <div className="bg-white/10 backdrop-blur-sm p-4 rounded-2xl border border-white/20 inline-block">
                <GraduationCap className="w-10 h-10 text-blue-300" />
              </div>
            </div>
            <h1 className="text-3xl md:text-4xl font-black mb-4 tracking-tight">
              INTUIT <span className="text-blue-400">LMS</span>
            </h1>
            <p className="text-lg text-blue-100/80 font-medium max-w-2xl leading-relaxed">
              Transform your learning experience with world-class education from your preferred institution.
            </p>
          </div>
        </div>
      </div>

      {/* Main Selection Area */}
      <div className="max-w-3xl mx-auto px-6 -mt-8 mb-20 w-full grow flex flex-col items-center">
        <div className="w-full bg-white rounded-xl shadow-lg p-8 md:p-10 border border-slate-100">
          <div className="max-w-2xl mx-auto text-center mb-8">
            <h2 className="text-2xl font-bold text-slate-900 mb-3">Identity Verification</h2>
            <p className="text-slate-500 text-sm">
              Please identify your institution to synchronize your academic records and curriculum.
            </p>
          </div>

          {/* Selection Trigger */}
          <div className="mb-8">
            <label className="block text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-2 ml-1">
              Academic Partner
            </label>
            <button
              onClick={() => setIsModalOpen(true)}
              className="w-full bg-slate-50 border border-slate-200 rounded-lg p-5 text-left flex items-center justify-between hover:border-blue-300 hover:bg-slate-100/50 transition-all group"
            >
              <div className="flex items-center gap-4">
                <div className={`w-10 h-10 rounded-lg flex items-center justify-center text-white font-bold transition-transform group-hover:scale-105 ${selectedInstitution ? '' : 'bg-slate-300'
                  }`} style={selectedInstitution ? { backgroundColor: selectedInstitution.primaryColor || '#2563eb' } : {}}>
                  {selectedInstitution ? selectedInstitution.name.charAt(0) : <Users className="w-5 h-5 text-white/70" />}
                </div>
                <div>
                  <span className={`block text-base font-bold ${selectedInstitution ? 'text-slate-900' : 'text-slate-400'
                    }`}>
                    {selectedInstitution ? selectedInstitution.name : 'Tap to select institution...'}
                  </span>
                  {selectedInstitution && (
                    <span className="text-[10px] font-bold text-blue-600 uppercase tracking-wider">
                      {selectedInstitution.learningMode === 'instructor_led' ? 'Instructor Led' : 'Self Paced'}
                    </span>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-[9px] font-black text-blue-600 uppercase tracking-wider">CHANGE</span>
                <ChevronDown className={`w-5 h-5 text-blue-600 transition-transform ${isModalOpen ? 'rotate-180' : ''}`} />
              </div>
            </button>
          </div>

          {/* Action Area */}
          <div className="max-w-md mx-auto">
            <button
              onClick={handleContinue}
              disabled={!selectedInstitution}
              className={`w-full py-4 px-8 rounded-lg font-bold text-base tracking-wide transition-all ${selectedInstitution
                ? 'bg-gradient-to-r from-blue-600 to-blue-700 text-white hover:opacity-90'
                : 'bg-slate-200 text-slate-400 cursor-not-allowed'
                }`}
            >
              SECURE SIGN-IN
            </button>
            <div className="flex items-center justify-center gap-6 mt-6">
              <div className="flex items-center gap-1.5 text-slate-400">
                <Lock className="w-3 h-3" />
                <span className="text-[10px] font-bold uppercase tracking-wider">Secure Access</span>
              </div>
              <div className="flex items-center gap-1.5 text-slate-400">
                <Zap className="w-3 h-3" />
                <span className="text-[10px] font-bold uppercase tracking-wider">Fast Sync</span>
              </div>
            </div>
          </div>
        </div>

        {/* Feature Highlights Grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-6 w-full max-w-4xl">
          {[
            { icon: BookOpen, label: 'Materials', color: 'text-blue-600' },
            { icon: Users, label: 'Expert Faculty', color: 'text-emerald-600' },
            { icon: Award, label: 'Accredited', color: 'text-amber-500' },
            { icon: Smartphone, label: 'Multi-App', color: 'text-indigo-600' }
          ].map((feature, i) => (
            <div key={i} className="bg-white p-4 rounded-lg border border-slate-100 flex flex-col items-center group hover:border-blue-100 transition-all">
              <feature.icon className={`w-5 h-5 ${feature.color} mb-2 transition-transform group-hover:scale-110`} />
              <span className="text-[9px] font-bold text-slate-700 uppercase tracking-wider">{feature.label}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Compact Minimalist Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 z-50 flex items-end md:items-center justify-center p-0 md:p-6 overflow-hidden">
          {/* Backdrop */}
          <div
            className="absolute inset-0 bg-slate-900/50 backdrop-blur-sm transition-opacity"
            onClick={() => setIsModalOpen(false)}
          ></div>

          {/* Modal Container */}
          <div className="bg-slate-50 w-full md:max-w-4xl max-h-[85vh] md:rounded-2xl shadow-2xl relative z-10 flex flex-col overflow-hidden animate-in slide-in-from-bottom md:zoom-in-95 duration-300">
            {/* Handle (Mobile) */}
            <div className="md:hidden w-9 h-1 bg-slate-300 rounded-full mx-auto mt-2 mb-3"></div>

            {/* Header */}
            <div className="p-5 md:p-6 border-b border-slate-200 shrink-0 bg-white">
              <div className="flex items-center justify-between gap-4">
                <div>
                  <div className="flex items-center gap-3 mb-1">
                    <h3 className="text-xl font-bold text-slate-900">Choose Institution</h3>
                    <span className="bg-blue-600/10 text-blue-700 text-[9px] font-black px-2 py-1 rounded uppercase tracking-wider">
                      {institutions.length} AVAILABLE
                    </span>
                  </div>
                  <p className="text-slate-500 text-xs">Select your learning partner from the list below</p>
                </div>
                <button
                  onClick={() => setIsModalOpen(false)}
                  className="w-10 h-10 rounded-full bg-slate-100 hover:bg-slate-200 flex items-center justify-center text-slate-600 transition-colors"
                >
                  <ChevronDown className="w-5 h-5" />
                </button>
              </div>
            </div>

            {/* Content - Scrollable List */}
            <div className="flex-1 overflow-y-auto p-5 md:p-6">
              <div className="max-w-3xl mx-auto space-y-3">
                {institutions.map((institution) => {
                  const isFeatured = institution.name === 'Intuit Online Academy';
                  const isSelected = selectedInstitution?.id === institution.id;

                  return (
                    <button
                      key={institution.id}
                      onClick={() => handleInstitutionSelect(institution)}
                      className={`group w-full text-left p-4 rounded-lg border transition-all ${isSelected
                        ? 'border-blue-600 bg-blue-50/50 shadow-md'
                        : 'border-slate-200 bg-white hover:border-blue-300 hover:shadow-sm'
                        }`}
                    >
                      <div className="flex items-center gap-4">
                        {/* Logo */}
                        <div
                          className="w-12 h-12 rounded-lg flex items-center justify-center text-white text-lg font-bold flex-shrink-0 transition-transform group-hover:scale-105"
                          style={{ backgroundColor: institution.primaryColor || '#2563eb' }}
                        >
                          {institution.logo ? (
                            <img src={institution.logo} alt={institution.name} className="w-full h-full object-cover rounded-lg" />
                          ) : (
                            institution.name.charAt(0)
                          )}
                        </div>

                        {/* Details */}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-2">
                            <h4 className={`text-base font-bold truncate ${isSelected ? 'text-blue-900' : 'text-slate-900'}`}>
                              {institution.name}
                            </h4>
                            {isFeatured && (
                              <span className="bg-amber-500 text-white text-[8px] font-black px-2 py-0.5 rounded uppercase tracking-wide flex-shrink-0">
                                PREMIUM PARTNER
                              </span>
                            )}
                          </div>

                          <div className="flex flex-col gap-1">
                            <div className="flex items-center gap-2">
                              <span className={`text-[9px] font-bold px-2 py-0.5 rounded uppercase tracking-wide ${institution.learningMode === 'self_paced' ? 'bg-emerald-600 text-white' : 'bg-blue-600 text-white'}`}>
                                {institution.learningMode === 'self_paced' ? 'SELF PACED COURSEWORK' : 'INSTRUCTOR LED'}
                              </span>
                            </div>
                            <div className="flex items-center gap-1.5 text-slate-500">
                              <RefreshCw className="w-3 h-3" />
                              <span className="text-[9px] font-semibold uppercase tracking-wide">MULTI-DEVICE SYNC ENABLED</span>
                            </div>
                          </div>
                        </div>

                        {/* Selection Indicator */}
                        <div className="flex items-center justify-center flex-shrink-0">
                          <div className={`w-7 h-7 rounded-full border-2 flex items-center justify-center transition-all ${isSelected
                            ? 'bg-blue-600 border-blue-600'
                            : 'border-slate-300 group-hover:border-blue-400'
                            }`}>
                            {isSelected && <Check className="w-4 h-4 text-white" />}
                          </div>
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default InstitutionSelection;
